/* eslint-disable react/no-array-index-key */
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import type { ReplacementValueItem } from "../types/api-types";
import { comparisonIdToString } from "./comparison-types";
import ExportCsvButton from "../../shared/export/export-csv-button";
import type { CsvColumn } from "../../shared/export/csv-export";

const exportColumns: CsvColumn<ReplacementValueItem>[] = [
  { header: "Value", value: (item) => item.value },
  { header: "New Value", value: (item) => item.newValue },
  {
    header: "Comparison",
    value: (item) =>
      item.description || comparisonIdToString(item.comparisonTypeId),
  },
];

interface ReplacementValuesTableProps {
  replacementValues: ReplacementValueItem[];
  onEdit: (index: number, item: ReplacementValueItem) => void;
  onDelete: (id: number, value: string) => void;
  isLoading?: boolean;
}

const ReplacementValuesTable = ({
  replacementValues,
  onEdit,
  onDelete,
  isLoading = false,
}: ReplacementValuesTableProps) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (replacementValues.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No data found
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-root": {
                fontWeight: 600,
                color: "primary.main",
                bgcolor: "grey.50",
              },
            }}
          >
            <TableCell>Value</TableCell>
            <TableCell>New Value</TableCell>
            <TableCell>Comparison</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>
              <ExportCsvButton
                rows={replacementValues}
                columns={exportColumns}
                fileName="replacement-values"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {replacementValues.map((item, index) => (
            <TableRow
              key={`replacement-value-${index}`}
              sx={{
                "&:hover": {
                  bgcolor: "grey.50",
                },
              }}
            >
              <TableCell>{item.value}</TableCell>
              <TableCell>{item.newValue}</TableCell>
              <TableCell>
                {item.description ||
                  comparisonIdToString(item.comparisonTypeId)}
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={() => onEdit(index, item)}
                  sx={{
                    color: "grey.600",
                    transition: "all 0.2s",
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: "primary.50",
                      transform: "scale(1.1)",
                    },
                  }}
                  aria-label="Edit replacement value"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDelete(item.id, item.value)}
                  sx={{
                    color: "grey.600",
                    transition: "all 0.2s",
                    "&:hover": {
                      color: "error.main",
                      bgcolor: "error.50",
                      transform: "scale(1.1)",
                    },
                  }}
                  aria-label="Delete replacement value"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReplacementValuesTable;
